// RFQ Field Mapping Configuration
// This file defines how external URL data should be mapped to RFQ fields
// Mappings can be customized in the Settings UI

export const defaultRFQMapping = {
  // Basic Information
  ref_code: {
    sourcePath: 'ref_code', // Backend returns ref_code directly
    transform: null,
    required: true,
  },
  title: {
    sourcePath: 'title',
    transform: (value) => {
      // Backend returns title as array sometimes, extract first item
      if (Array.isArray(value)) return value[0];
      return value;
    },
    required: true,
  },
  client_id: {
    sourcePath: 'client_id',
    transform: null, // Backend returns agency name, will need to lookup client_id
    required: false,
  },

  // Clearance & Work Arrangement
  clearance_required: {
    sourcePath: 'clearance_required',
    transform: null, // Backend already normalizes this
    required: false,
  },
  work_arrangement: {
    sourcePath: 'work_arrangement',
    transform: null,
    required: false,
  },

  // Locations
  locations: {
    sourcePath: 'locations',
    transform: (value) => {
      // Ensure locations is always an array
      if (Array.isArray(value)) return value;
      if (typeof value === 'string') return [value];
      return [];
    },
    required: false,
  },

  // Dates
  internal_close_datetime: {
    sourcePath: 'internal_close_datetime',
    transform: null, // Backend returns as string, keep as-is for now
    required: false,
  },
  client_close_datetime: {
    sourcePath: 'client_close_datetime',
    transform: null, // Backend returns as string, keep as-is for now
    required: true,
  },
  estimated_start_date: {
    sourcePath: 'estimated_start_date',
    transform: null, // Backend returns as string, keep as-is for now
    required: false,
  },

  // Terms
  initial_term_months: {
    sourcePath: 'initial_term_months',
    transform: null, // Backend already extracts the number
    required: false,
  },
  extension_options: {
    sourcePath: 'extension_options',
    transform: (value, fullData) => {
      // If extension_options is already provided, use it
      if (value) return value;

      // Otherwise, try to construct it from requirements array
      if (fullData?.requirements && Array.isArray(fullData.requirements)) {
        const extensionTerm = fullData.requirements.find(
          req => req.label && req.label.toLowerCase().includes('extension term')
        );
        const numberOfExtensions = fullData.requirements.find(
          req => req.label && req.label.toLowerCase().includes('number of extension')
        );

        if (extensionTerm && numberOfExtensions) {
          // Extract numeric values
          const termValue = extensionTerm.value; // e.g., "12 months"
          const numExtensions = numberOfExtensions.value; // e.g., "1"

          // Format as "1 x 12 months"
          return `${numExtensions} x ${termValue}`;
        }
      }

      return value;
    },
    required: false,
  },

  // Description and criteria
  description: {
    sourcePath: 'description',
    transform: null,
    required: false,
  },
  selection_criteria: {
    sourcePath: 'selection_criteria',
    transform: null,
    required: false,
  },

  // Status
  status: {
    sourcePath: 'status',
    transform: (value) => {
      // Default to 'not_invited' for new RFQs
      return value || 'not_invited';
    },
    required: false,
  },
};

// Function to apply mapping to fetched data
export const mapExternalDataToRFQ = (externalData, mapping = defaultRFQMapping) => {
  const mappedData = {};

  for (const [rfqField, config] of Object.entries(mapping)) {
    try {
      // Get value from source data using path
      let value = getNestedValue(externalData, config.sourcePath);

      // Apply transformation if provided
      if (config.transform && typeof config.transform === 'function') {
        // Pass both the value and the full external data to the transform function
        value = config.transform(value, externalData);
      }

      // Only set if value exists or field is required
      if (value !== undefined && value !== null) {
        mappedData[rfqField] = value;
      }
    } catch (error) {
      console.warn(`Error mapping field ${rfqField}:`, error);
    }
  }

  return mappedData;
};

// Helper to get nested object values using dot notation
const getNestedValue = (obj, path) => {
  if (!path) return undefined;

  return path.split('.').reduce((current, key) => {
    return current?.[key];
  }, obj);
};

// Export for settings UI
export const getRFQFields = () => {
  return Object.keys(defaultRFQMapping);
};

export const getFieldConfig = (fieldName) => {
  return defaultRFQMapping[fieldName];
};
